import React, { useState } from 'react';
import { Avatar, Upload, Modal, message, Spin } from 'antd';
import { CameraOutlined, UserOutlined } from '@ant-design/icons';
import httpClient from '../../utils/HttpClient';
import { UPLOAD_AVATAR_URL } from '../../constants/Url';
import { useTranslation } from 'react-i18next';

const ProfileAvatar = ({ name = '', size = 80, imageUrl, onImageChange, debouncedFetchAll }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();

  const camSize = size > 100 ? 36 : 28;
  const camIconSize = size > 100 ? 16 : 13;

  // ✅ Upload handler (API)
  const beforeUpload = async (file) => {
    if (!file.type.startsWith('image/')) {
      message.error(t('text_only_image_allow'));
      return false;
    }

    if (file.size / 1024 / 1024 >= 5) {
      message.error(t('text_image_small_than_5mb'));
      return false;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);

    try {
      const res = await httpClient.post(UPLOAD_AVATAR_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res?.data?.status === 200) {
        const imageUrl = res?.data?.data?.url; // ⚠️ adjust if API structure differs

        onImageChange?.(imageUrl); // ✅ update parent
        message.success(t('text_profile_photo_updated'));
        debouncedFetchAll()
      } else {
        message.error(t(res?.data?.message || 'text_upload_failed'));
      }
    } catch (error) {
      console.error(error);
      message.error(t('text_upload_failed'));
    } finally {
      setUploading(false);
    }

    return false; // prevent default upload
  };

  // Camera button
  const CameraBtn = ({ btnSize, iconSize }) => (
    <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
      <div
        style={{
          width: btnSize,
          height: btnSize,
          borderRadius: '50%',
          background: '#fff',
          border: '1.5px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          cursor: 'pointer',
        }}
      >
        {uploading ? (
          <Spin size="small" />
        ) : (
          <CameraOutlined style={{ fontSize: iconSize, color: '#444' }} />
        )}
      </div>
    </Upload>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>

      {/* Avatar */}
      <div
        onClick={() => imageUrl && setPreviewOpen(true)}
        style={{ cursor: imageUrl ? 'pointer' : 'default' }}
      >
        <Avatar
          size={size}
          src={imageUrl}
          icon={!imageUrl && <UserOutlined />}
          style={{
            border: '3px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}
        />
      </div>

      {/* Camera Button */}
      <div style={{ position: 'absolute', bottom: 0, right: 0 }}>
        <CameraBtn btnSize={camSize} iconSize={camIconSize} />
      </div>

      {/* Preview Modal */}
      <Modal
        open={previewOpen}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        centered
        width={420}
      >
        <div style={{ padding: '28px 0 16px', textAlign: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Avatar
              size={180}
              src={imageUrl}
              style={{ border: '3px solid #f0f0f0' }}
            />
            <div style={{ position: 'absolute', bottom: 4, right: 4 }}>
              <CameraBtn btnSize={36} iconSize={17} />
            </div>
          </div>

          <div style={{ fontWeight: 700, fontSize: 20, marginTop: 16 }}>
            {name}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProfileAvatar;